import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Divider,
  Grow,
  Zoom,
  Tabs,
  Tab,
  Badge,
  LinearProgress
} from '@mui/material';

import {
  Search,
  Visibility,
  CheckCircle,
  Pending,
  Download,
  Refresh,
  Clear,
  Assignment,
  CalendarToday,
  Verified,
  Person,
  Business,
  ThumbUp,
  LocalShipping,
  Receipt,
  AssignmentTurnedIn,
  Warning,
  HourglassEmpty,
  DeleteOutline,
  Block,
  DoneAll,
  Schedule,
  TrendingUp,
  Storefront,
  Inventory,
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../components/sidebars/sidebar_gm';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  '& .MuiTable-root': {
    minWidth: 1000,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusString = String(status || 'Pending').toLowerCase();
  
  let config = {};
  
  if (statusString === 'rejected') {
    config = {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      color: theme.palette.error.main,
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    };
  } else if (statusString === 'approved by gm') {
    config = {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  } else if (statusString === 'approved by dept head') {
    config = {
      backgroundColor: alpha(theme.palette.info.main, 0.12),
      color: theme.palette.info.main,
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
    };
  } else if (statusString === 'issued') {
    config = {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    };
  } else if (statusString === 'received') {
    config = {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  } else {
    config = {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
      color: theme.palette.warning.main,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
    };
  }
  
  return {
    borderRadius: '20px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    ...config
  };
});

const GMRequisitionApproval = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [userRole, setUserRole] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('request_date');
  const [order, setOrder] = useState('desc');
  
  // Data states for different tabs
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [hodApproved, setHodApproved] = useState([]);
  const [issuedRequisitions, setIssuedRequisitions] = useState([]);
  const [receivedRequisitions, setReceivedRequisitions] = useState([]);
  const [rejectedRequisitions, setRejectedRequisitions] = useState([]);
  const [allRequisitions, setAllRequisitions] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    pending_gm: 0,
    hod_approved: 0,
    issued: 0,
    received: 0,
    rejected: 0,
    total: 0
  });
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Fetch all requisitions data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_gm_requisitions.php?${params}`);
      
      if (response.data.success) {
        setPendingApprovals(response.data.pending_gm || []);
        setHodApproved(response.data.hod_approved || []);
        setIssuedRequisitions(response.data.issued || []);
        setReceivedRequisitions(response.data.received || []);
        setRejectedRequisitions(response.data.rejected || []);
        setAllRequisitions(response.data.all || []);
        setStatistics(response.data.statistics || {
          pending_gm: 0,
          hod_approved: 0,
          issued: 0,
          received: 0,
          rejected: 0,
          total: 0
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to fetch requisitions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, departmentFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, departmentFilter, activeTab]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const role = localStorage.getItem('role');
    
    setLoggedInUser(userName || userEmail || 'GM');
    setUserRole(role || 'staff');
  }, []);

  // Check if user is authorized as GM
  const isAuthorizedGM = () => {
    return userRole === 'gm' || userRole === 'admin' || userRole === 'general_manager';
  };

  // Approve requisition as GM
  const handleApproveRequisition = async () => {
    if (!selectedRequisition) return;
    
    setLoading(true);
    try {
      const payload = {
        id: selectedRequisition.id,
        action: 'approve_gm',
        user_name: loggedInUser
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/update_requisition_status.php`,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Requisition #${selectedRequisition.id} approved successfully`,
          severity: 'success'
        });
        handleCloseApproveDialog();
        fetchAllData();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error approving requisition:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to approve requisition',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel based on current tab
  const handleExportToExcel = () => {
    let exportData = [];
    let sheetName = '';
    
    switch(activeTab) {
      case 0:
        exportData = pendingApprovals;
        sheetName = 'Pending_GM_Approval';
        break;
      case 1:
        exportData = hodApproved;
        sheetName = 'HOD_Approved';
        break;
      case 2:
        exportData = issuedRequisitions;
        sheetName = 'Issued_Requisitions';
        break;
      case 3:
        exportData = receivedRequisitions;
        sheetName = 'Received_Requisitions';
        break;
      case 4:
        exportData = rejectedRequisitions;
        sheetName = 'Rejected_Requisitions';
        break;
      default:
        exportData = allRequisitions;
        sheetName = 'All_Requisitions';
    }
    
    const formattedData = exportData.map(item => ({
      'ID': item.id,
      'Department': item.department,
      'Item Name': item.item_name,
      'Quantity': item.quantity_requested,
      'Unit': item.unit_of_measurement || 'N/A',
      'Requested By': item.requested_by,
      'Request Date': item.request_date,
      'Approved By HOD': item.approved_by_dept_head || 'Pending',
      'Approved By GM': item.approved_by_GM || 'Pending',
      'Issued By': item.issued_by || 'Pending',
      'Received By': item.received_by || 'Pending',
      'Status': item.status,
      'Notes': item.notes || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${sheetName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleOpenViewDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setViewDialogOpen(true);
  };

  const handleOpenApproveDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setApprovalNotes('');
    setApproveDialogOpen(true);
  };

  const handleCloseViewDialog = () => setViewDialogOpen(false);
  const handleCloseApproveDialog = () => setApproveDialogOpen(false);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_requested') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'request_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDepartmentFilter('all');
    setPage(0);
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    switch(activeTab) {
      case 0: return pendingApprovals;
      case 1: return hodApproved;
      case 2: return issuedRequisitions;
      case 3: return receivedRequisitions;
      case 4: return rejectedRequisitions;
      default: return allRequisitions;
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Rejected': return <Block fontSize="small" />;
      case 'Approved by GM': return <Verified fontSize="small" />;
      case 'Approved by Dept Head': return <ThumbUp fontSize="small" />;
      case 'Issued': return <LocalShipping fontSize="small" />;
      case 'Received': return <CheckCircle fontSize="small" />;
      default: return <Pending fontSize="small" />;
    }
  };

  // Render statistics cards
  const renderStatisticsCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '0ms' }}>
          <StyledCard onClick={() => setActiveTab(0)}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Pending GM Approval
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="warning.main">
                    {statistics.pending_gm}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, width: 56, height: 56 }}>
                  <HourglassEmpty sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Awaiting your approval
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '100ms' }}>
          <StyledCard onClick={() => setActiveTab(1)}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    HOD Approved
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="info.main">
                    {statistics.hod_approved}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, width: 56, height: 56 }}>
                  <ThumbUp sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Waiting for GM approval
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
          <StyledCard onClick={() => setActiveTab(2)}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Issued
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="primary.main">
                    {statistics.issued}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 56, height: 56 }}>
                  <LocalShipping sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Items issued to departments
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
          <StyledCard onClick={() => setActiveTab(3)}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Received
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="success.main">
                    {statistics.received}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, width: 56, height: 56 }}>
                  <AssignmentTurnedIn sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Completed requisitions
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '400ms' }}>
          <StyledCard onClick={() => setActiveTab(4)}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Rejected
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="error.main">
                    {statistics.rejected}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, width: 56, height: 56 }}>
                  <Block sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Rejected requisitions
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
    </Grid>
  );

  // Render requisitions table
  const renderRequisitionsTable = () => {
    const currentData = getCurrentData();
    const showApproveButton = activeTab === 0; // Only show approve button for pending GM approvals
    
    return (
      <StyledTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Item Name</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>HOD Approval</TableCell>
              <TableCell>GM Approval</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : currentData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No requisitions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortData(currentData).map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>#{item.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.department} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Person sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                      </Avatar>
                      <Typography variant="body2">{item.requested_by}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Inventory sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {Number(item.quantity_requested).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.request_date}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {item.approved_by_dept_head ? (
                      <Tooltip title={`Approved by: ${item.approved_by_dept_head}`}>
                        <Chip 
                          label="Approved" 
                          size="small" 
                          color="success" 
                          icon={<CheckCircle fontSize="small" />}
                          sx={{ height: 24 }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="Pending" size="small" color="warning" icon={<Pending fontSize="small" />} sx={{ height: 24 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.approved_by_GM ? (
                      <Tooltip title={`Approved by: ${item.approved_by_GM}`}>
                        <Chip 
                          label="Approved" 
                          size="small" 
                          color="success" 
                          icon={<Verified fontSize="small" />}
                          sx={{ height: 24 }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="Pending" size="small" color="warning" icon={<Pending fontSize="small" />} sx={{ height: 24 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip 
                      label={item.status} 
                      status={item.status} 
                      size="small"
                      icon={getStatusIcon(item.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleOpenViewDialog(item)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {showApproveButton && !item.approved_by_GM && (
                        <Tooltip title="Approve Requisition">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleOpenApproveDialog(item)}
                          >
                            <Verified fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={currentData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </StyledTableContainer>
    );
  };

  // Check authorization
  if (!isAuthorizedGM()) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error" variant="filled">
          Access Denied. General Manager privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              <Verified sx={{ mr: 2, verticalAlign: 'middle', fontSize: 32, color: theme.palette.primary.main }} />
              GM Requisition Approval Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review and approve requisitions from all departments. Monitor the entire requisition workflow.
            </Typography>
          </Box>

          {/* Statistics Cards */}
          {renderStatisticsCards()}

          {/* Main Content */}
          <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            {/* Header Bar with Tabs */}
            <Box sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 3,
                  pt: 2,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 48,
                  }
                }}
              >
                <Tab 
                  icon={<Badge badgeContent={statistics.pending_gm} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 18, minWidth: 18 } }}>
                    <HourglassEmpty />
                  </Badge>} 
                  label="Pending GM Approval" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<Badge badgeContent={statistics.hod_approved} color="info">
                    <ThumbUp />
                  </Badge>} 
                  label="HOD Approved" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<Badge badgeContent={statistics.issued} color="primary">
                    <LocalShipping />
                  </Badge>} 
                  label="Issued" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<Badge badgeContent={statistics.received} color="success">
                    <AssignmentTurnedIn />
                  </Badge>} 
                  label="Received" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<Badge badgeContent={statistics.rejected} color="error">
                    <Block />
                  </Badge>} 
                  label="Rejected" 
                  iconPosition="start"
                />
              </Tabs>
              
              {/* Search and Filters */}
              <Box sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      placeholder="Search by item name, requester, department..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Filter by Department</InputLabel>
                      <Select
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e.target.value)}
                        label="Filter by Department"
                      >
                        <MenuItem value="all">All Departments</MenuItem>
                        <MenuItem value="ICT">ICT</MenuItem>
                        <MenuItem value="Store Keeping">Store Keeping</MenuItem>
                        <MenuItem value="Account & Auditing">Account & Auditing</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                        <MenuItem value="Restaurant">Restaurant</MenuItem>
                        <MenuItem value="Spa">Spa</MenuItem>
                        <MenuItem value="Media">Media</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExportToExcel}
                        size="small"
                      >
                        Export
                      </Button>
                      <IconButton onClick={() => fetchAllData()} size="small">
                        <Refresh />
                      </IconButton>
                      {(searchQuery || departmentFilter !== 'all') && (
                        <Button size="small" onClick={clearFilters} startIcon={<Clear />}>
                          Clear
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Table */}
            {renderRequisitionsTable()}
          </Paper>
        </Box>
      </Box>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span" fontWeight={600}>
              Requisition Details #{selectedRequisition?.id}
            </Typography>
            {selectedRequisition && (
              <StatusChip label={selectedRequisition.status} status={selectedRequisition.status} size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequisition && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                Request Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.department}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1">{selectedRequisition.requested_by}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Item Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.item_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Quantity Requested</Typography>
                  <Typography variant="body1">
                    {Number(selectedRequisition.quantity_requested).toLocaleString()} {selectedRequisition.unit_of_measurement || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1">{selectedRequisition.request_date}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1">{selectedRequisition.updated_at || selectedRequisition.request_date}</Typography>
                </Grid>
                {selectedRequisition.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes / Purpose</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                      <Typography variant="body2">{selectedRequisition.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                Approval Workflow
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.approved_by_dept_head ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <ThumbUp sx={{ fontSize: 32, color: selectedRequisition.approved_by_dept_head ? 'success.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Dept Head Approval</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.approved_by_dept_head || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.approved_by_GM ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <Verified sx={{ fontSize: 32, color: selectedRequisition.approved_by_GM ? 'success.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">GM Approval</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.approved_by_GM || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.issued_by ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <LocalShipping sx={{ fontSize: 32, color: selectedRequisition.issued_by ? 'primary.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Issued By</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.issued_by || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.received_by ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <AssignmentTurnedIn sx={{ fontSize: 32, color: selectedRequisition.received_by ? 'success.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Received By</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.received_by || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {selectedRequisition && activeTab === 0 && !selectedRequisition.approved_by_GM && (
            <Button 
              variant="contained" 
              color="success"
              startIcon={<Verified />}
              onClick={() => {
                handleCloseViewDialog();
                handleOpenApproveDialog(selectedRequisition);
              }}
            >
              Approve Requisition
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Verified color="success" />
            <Typography variant="h6" component="span" fontWeight={600}>
              Approve Requisition as GM
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to approve this requisition?
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
            <Typography variant="body2">
              <strong>Department:</strong> {selectedRequisition?.department}<br />
              <strong>Item:</strong> {selectedRequisition?.item_name}<br />
              <strong>Quantity:</strong> {selectedRequisition?.quantity_requested} {selectedRequisition?.unit_of_measurement}<br />
              <strong>Requested By:</strong> {selectedRequisition?.requested_by}<br />
              <strong>Approved By HOD:</strong> {selectedRequisition?.approved_by_dept_head || 'N/A'}
            </Typography>
          </Paper>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Approval Notes (Optional)"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Add any remarks or conditions for this approval..."
            sx={{ mt: 2 }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Once approved, this requisition will be forwarded to the store keeper for issuance.
              Your name will be recorded as the approving GM.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveRequisition}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Verified />}
          >
            {loading ? 'Processing...' : 'Confirm GM Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Grow}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GMRequisitionApproval;